import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  PButtonPure,
  PIcon,
  PText,
} from '@porsche-design-system/components-react';
import { useDropzone, type FileRejection } from 'react-dropzone';

import { FileSize } from './FileSize';
import { FileType } from './FileType';

export const Dropzone = styled('div', {
  base: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'xl',
    alignItems: 'center',
    padding: 'lg',
    borderWidth: '1px',
    borderColor: 'border.contrastLower',
    borderStyle: 'dashed',
    backgroundColor: 'background.frosted',
    color: 'text.contrastHigh',
    outline: 'none',
    cursor: 'pointer',
  },
});

const Files = styled('ul', {
  base: {
    padding: '0',
    margin: '0',
    display: 'block',
    width: '0',
    minWidth: '100%',
  },
});

const File = styled('li', {
  base: {
    listStyleType: 'none',
    display: 'flex',
    position: 'relative',
    paddingTop: 'sm',
    paddingBottom: 'sm',
    borderBottom: '1px solid',
    borderBottomColor: 'border.contrastLow',
    gap: 'sm',
  },
});

const FileName = styled('span', {
  base: {
    display: 'inline-block',
    paddingLeft: 'xs',
    paddingRight: 'xs',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    flex: 1,
  },
});

const ProgressBar = styled('div', {
  base: {
    height: '6px',
    bottom: 0,
    left: 0,
    backgroundColor: 'background.successFrosted',
    borderRadius: 'xl',
    position: 'absolute',
    transition: 'width 0.5s 0s ease',
  },
});

const DropzoneInnerWrapper = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'xs',
  },
});

const SortableFile = ({
  id,
  sortable,
  children,
  ...props
}: {
  id: string;
  sortable: boolean;
  children: React.ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!sortable) return <File {...props}>{children}</File>;

  return (
    <File ref={setNodeRef} {...props} style={style} {...attributes}>
      <PIcon
        name="menu-lines"
        ref={setActivatorNodeRef}
        {...listeners}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        data-testid="draggable-handle"
      />
      {children}
    </File>
  );
};

export const DropzoneInner = ({
  isDragActive,
  i18n,
}: {
  isDragActive: boolean;
  i18n: {
    description: string;
    dragDescription: string;
    allowedTypes: string;
  };
}) => (
  <DropzoneInnerWrapper>
    <PIcon name="upload" />
    <PText align="center">
      {isDragActive ? i18n.dragDescription : i18n.description}
    </PText>
    <PText size="2xs" align="center">
      {i18n.allowedTypes}
    </PText>
  </DropzoneInnerWrapper>
);

export const DropzoneFiles = ({
  files,
  existingFiles,
  sortableFiles,
  progress,
  onDelete,
  onDeleteExisting,
  canDelete,
  onSortChange,
}: {
  files?: {
    id: string;
    file: File;
  }[];
  existingFiles?: {
    id: string;
    name: string;
    size: number;
    type: string;
  }[];
  sortableFiles?: (
    | {
        id: string;
        file: File;
      }
    | {
        id: string;
        name: string;
        size: number;
        type: string;
      }
  )[];
  progress: number[];
  onDelete?: (index: number) => void;
  onDeleteExisting?: (id: string) => void;
  canDelete?: (
    file:
      | {
          id: string;
          file: File;
        }
      | {
          id: string;
          name: string;
          size: number;
          type: string;
        },
  ) => boolean;
  onSortChange?: (opt: { oldIndex: number; newIndex: number }) => void;
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8, // See: https://github.com/clauderic/dnd-kit/issues/591#issuecomment-1017050816
      },
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={(event) => {
        if (!event.over || event.active.id === event.over.id) return;

        const oldIndex = sortableFiles!.findIndex(
          (i) => i.id === event.active.id,
        );
        const newIndex = sortableFiles!.findIndex(
          (i) => i.id === event.over!.id,
        );

        onSortChange?.({ oldIndex, newIndex });
      }}
    >
      <SortableContext
        items={sortableFiles ?? []}
        strategy={verticalListSortingStrategy}
      >
        <Files>
          {(sortableFiles ?? [...(files ?? []), ...(existingFiles ?? [])]).map(
            (file, index) =>
              // new files
              'file' in file ? (
                <SortableFile
                  key={`${file.file.name}_${index}`}
                  aria-label={file.file.name}
                  data-testid="upload"
                  data-cy="upload"
                  sortable={!!sortableFiles}
                  id={file.id}
                >
                  {progress[index] > 0 && (
                    <ProgressBar
                      aria-valuenow={progress[index]}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      role="progressbar"
                      style={{ width: `${progress[index]}%` }}
                    />
                  )}
                  <FileType value={file.file.type} />
                  <FileName>{file.file.name}</FileName>
                  <span>
                    <FileSize value={file.file.size} />
                  </span>
                  {onDelete && (
                    <PButtonPure
                      icon="delete"
                      size="sm"
                      onClick={() => onDelete(index)}
                      type="button"
                      hideLabel
                      disabled={canDelete ? !canDelete(file) : false}
                    >
                      {'delete attachment'}
                    </PButtonPure>
                  )}
                </SortableFile>
              ) : (
                // already existing files
                <SortableFile
                  key={file.id}
                  aria-label={file.name}
                  sortable={!!sortableFiles}
                  id={file.id}
                >
                  <FileType value={file.type} />
                  <FileName>{file.name}</FileName>
                  <span>
                    <FileSize value={file.size} />
                  </span>
                  {onDeleteExisting && (
                    <PButtonPure
                      icon="delete"
                      size="sm"
                      onClick={() => onDeleteExisting(file.id)}
                      type="button"
                      hideLabel
                      disabled={canDelete ? !canDelete(file) : false}
                    >
                      {'delete attachment'}
                    </PButtonPure>
                  )}
                </SortableFile>
              ),
          )}
        </Files>
      </SortableContext>
    </DndContext>
  );
};

export { useDropzone };

export type { FileRejection };
